/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, ReactNode } from 'react';
import { AppLanguage } from '../types';
import { TRANSLATIONS, TranslationKey } from '../data/translations';
import { ENGLISH_ALPHABET, ENGLISH_WORDS, EXPLORER_ALPHABET_WORDS } from '../data/enData';
import { URDU_ALPHABET, URDU_WORDS, EXPLORER_URDU_ALPHABET_WORDS } from '../data/urData';
import { ARABIC_ALPHABET, ARABIC_WORDS } from '../data/arData';

export interface LanguageData {
  alphabet: any[];
  words: any[];
  explorerWords: Record<string, any[]>;
}

export interface LanguageStateContextProps {
  currentLanguage: AppLanguage;
  isRtl: boolean;
  t: (key: TranslationKey, params?: Record<string, string>) => string;
  languageData: LanguageData | null;
  isLoadingData: boolean;
  loadedDatasets: Record<AppLanguage, LanguageData | null>;
}

export interface LanguageActionsContextProps {
  setLanguage: (lang: AppLanguage) => void;
  changeLanguage: (lang: AppLanguage) => void;
  loadLanguageData: (lang: AppLanguage) => Promise<LanguageData>;
  getAlphabetForLang: (lang: AppLanguage) => any[];
  getWordsForLang: (lang: AppLanguage) => any[];
  getExplorerWordsForLang: (lang: AppLanguage) => Record<string, any[]>;
}

export interface LanguageContextProps extends LanguageStateContextProps, LanguageActionsContextProps {}

export const LanguageStateContext = createContext<LanguageStateContextProps | undefined>(undefined);
export const LanguageActionsContext = createContext<LanguageActionsContextProps | undefined>(undefined);
export const LanguageContext = createContext<LanguageContextProps | undefined>(undefined);

const LOCAL_LANG_KEY = 'llw_lang_v1';

const INITIAL_DATASETS: Record<AppLanguage, LanguageData> = {
  en: {
    alphabet: ENGLISH_ALPHABET,
    words: ENGLISH_WORDS,
    explorerWords: EXPLORER_ALPHABET_WORDS,
  },
  ur: {
    alphabet: URDU_ALPHABET,
    words: URDU_WORDS,
    explorerWords: EXPLORER_URDU_ALPHABET_WORDS,
  },
  ar: {
    alphabet: ARABIC_ALPHABET,
    words: ARABIC_WORDS,
    explorerWords: {},
  }
};

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentLanguage, setLanguageState] = useState<AppLanguage>('en');
  const [loadedDatasets, setLoadedDatasets] = useState<Record<AppLanguage, LanguageData | null>>(INITIAL_DATASETS);
  const [isLoadingData, setIsLoadingData] = useState<boolean>(false);

  const loadedDatasetsRef = useRef(loadedDatasets);
  useEffect(() => {
    loadedDatasetsRef.current = loadedDatasets;
  }, [loadedDatasets]);

  const loadLanguageData = useCallback(async (lang: AppLanguage): Promise<LanguageData> => {
    if (loadedDatasetsRef.current[lang]) {
      return loadedDatasetsRef.current[lang]!;
    }
    const data = INITIAL_DATASETS[lang] || INITIAL_DATASETS.en;
    setLoadedDatasets(prev => ({
      ...prev,
      [lang]: data
    }));
    setIsLoadingData(false);
    return data;
  }, []);

  useEffect(() => {
    const cachedLang = (localStorage.getItem(LOCAL_LANG_KEY) as AppLanguage) || 'en';
    setLanguageState(cachedLang);
  }, []);

  const setLanguage = useCallback((lang: AppLanguage) => {
    setLanguageState(lang);
    localStorage.setItem(LOCAL_LANG_KEY, lang);
  }, []);

  const changeLanguage = setLanguage;

  const isRtl = currentLanguage === 'ur' || currentLanguage === 'ar';

  const t = useCallback((key: TranslationKey, params?: Record<string, string>): string => {
    const dict = TRANSLATIONS[currentLanguage] || TRANSLATIONS.en;
    let translation = dict[key] || TRANSLATIONS.en[key] || '';
    
    if (params) {
      Object.entries(params).forEach(([paramKey, paramVal]) => {
        translation = translation.replace(`{${paramKey}}`, paramVal);
      });
    }
    
    return translation;
  }, [currentLanguage]);

  const getAlphabetForLang = useCallback((lang: AppLanguage): any[] => {
    return loadedDatasetsRef.current[lang]?.alphabet || [];
  }, []);

  const getWordsForLang = useCallback((lang: AppLanguage): any[] => {
    return loadedDatasetsRef.current[lang]?.words || [];
  }, []);

  const getExplorerWordsForLang = useCallback((lang: AppLanguage): Record<string, any[]> => {
    return loadedDatasetsRef.current[lang]?.explorerWords || {};
  }, []);

  const stateValue = useMemo<LanguageStateContextProps>(() => ({
    currentLanguage,
    isRtl,
    t,
    languageData: loadedDatasets[currentLanguage],
    isLoadingData,
    loadedDatasets,
  }), [currentLanguage, isRtl, t, loadedDatasets, isLoadingData]);

  const actionsValue = useMemo<LanguageActionsContextProps>(() => ({
    setLanguage,
    changeLanguage,
    loadLanguageData,
    getAlphabetForLang,
    getWordsForLang,
    getExplorerWordsForLang,
  }), [setLanguage, changeLanguage, loadLanguageData, getAlphabetForLang, getWordsForLang, getExplorerWordsForLang]);

  const combinedValue = useMemo<LanguageContextProps>(() => ({
    ...stateValue,
    ...actionsValue,
  }), [stateValue, actionsValue]);

  return (
    <LanguageActionsContext.Provider value={actionsValue}>
      <LanguageStateContext.Provider value={stateValue}>
        <LanguageContext.Provider value={combinedValue}>
          {children}
        </LanguageContext.Provider>
      </LanguageStateContext.Provider>
    </LanguageActionsContext.Provider>
  );
};

export const useLanguageState = (): LanguageStateContextProps => {
  const context = useContext(LanguageStateContext);
  if (!context) {
    throw new Error('useLanguageState must be used within a LanguageProvider');
  }
  return context;
};

export const useLanguageActions = (): LanguageActionsContextProps => {
  const context = useContext(LanguageActionsContext);
  if (!context) {
    throw new Error('useLanguageActions must be used within a LanguageProvider');
  }
  return context;
};

export const useLanguage = (): LanguageContextProps => {
  const stateContext = useContext(LanguageStateContext);
  const actionsContext = useContext(LanguageActionsContext);
  if (stateContext && actionsContext) {
    return {
      ...stateContext,
      ...actionsContext,
    };
  }
  const legacy = useContext(LanguageContext);
  if (!legacy) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return legacy;
};
